// src/shared/lib/date/date.ts

/** Returns true if date is a valid Date instance */
const isValidDate = (d: Date): boolean => !Number.isNaN(d.getTime());

/**
 * Parses input into Date (generic, reusable, no domain rules).
 * Supported:
 * - Date
 * - timestamp (ms)
 * - ISO strings (native Date parser)
 * - YYYY-MM-DD (parsed as LOCAL date to avoid timezone shifts)
 * - DD.MM.YYYY (common DE/UA format)
 */
function toDate(value: unknown): Date | null {
  if (value instanceof Date) return isValidDate(value) ? value : null;

  if (typeof value === 'number') {
    const d = new Date(value);
    return isValidDate(d) ? d : null;
  }

  if (typeof value === 'string') {
    const s = value.trim();
    if (!s || s.toLowerCase() === 'null') return null;

    // 1) YYYY-MM-DD (parse as LOCAL date to avoid UTC shift)
    const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymd) {
      const [, yyyy, mm, dd] = ymd;
      const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
      return isValidDate(d) ? d : null;
    }

    // 2) DD.MM.YYYY
    const dmy = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (dmy) {
      const [, dd, mm, yyyy] = dmy;
      const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
      return isValidDate(d) ? d : null;
    }

    // 3) ISO / native parser fallback
    const dIso = new Date(s);
    return isValidDate(dIso) ? dIso : null;
  }

  return null;
}

/** Formats Date to YYYY-MM-DD for <input type="date"> */
function toInputDate(d: Date): string {
  if (!isValidDate(d)) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * ✅ Use this for input fields (<input type="date">) when you can receive:
 * - Date
 * - ISO string
 * - YYYY-MM-DD
 * - null/undefined
 */
export function formatDateForInput(value?: string | Date | null): string {
  if (value == null) return '';
  const d = toDate(value);
  return d ? toInputDate(d) : '';
}

/** Formats Date as "23.02.2026" (DD.MM.YYYY). If input missing -> today. If invalid -> '' */
export function formatDateDDMMYYYY(input?: unknown): string {
  const d = input == null ? new Date() : toDate(input);
  if (!d) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

/** Long German date, e.g. "23. Februar 2026" */
export function formatDateDELong(value: Date | string): string {
  const d = value instanceof Date ? value : toDate(value);
  if (!d) return '';
  return new Intl.DateTimeFormat('de-DE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d);
}

/**
 * Converts a Date to "YYYY-MM-DD" using LOCAL parts (no time, no Z).
 * Useful for sending date-only to API or storing locally.
 */
export function toLocalISODateOnly(date: Date): string {
  if (!isValidDate(date)) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Builds "YYYY-MM" from year & month.
 * Returns '' if inputs missing or invalid.
 */
export function formatMonthValue(year?: number | null, month?: number | null): string {
  if (year == null || month == null) return '';
  if (!Number.isInteger(year) || !Number.isInteger(month)) return '';
  if (month < 1 || month > 12) return '';
  return `${year}-${String(month).padStart(2, '0')}`;
}

/** Combines a YYYY-MM-DD day string with hours/minutes into a local ISO string. */
export function combineDayAndTimeIso(dayIso: string, hours: number, minutes: number): string {
  const d = new Date(`${dayIso}T00:00:00`)
  d.setHours(hours, minutes, 0, 0)
  return d.toISOString()
}

/**
 * Converts an ISO string (or Date) into a value suitable for
 * `<input type="datetime-local">`, rendered in the browser's local timezone.
 * Returns '' when the input is missing / invalid.
 */
export function toDatetimeLocal(value?: string | Date | null): string {
  if (value == null) return ''
  const d = toDate(value)
  if (!d) return ''
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${y}-${m}-${day}T${hh}:${mm}`
}

/**
 * Reverse of `toDatetimeLocal` — parses a `YYYY-MM-DDTHH:MM` value (local time)
 * from a `<input type="datetime-local">` and returns a full UTC ISO string.
 * Returns null when the input is empty or unparseable.
 */
export function fromDatetimeLocal(value: string): string | null {
  if (!value) return null
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/)
  if (!m) return null
  const [, y, mo, d, hh, mm] = m
  const local = new Date(Number(y), Number(mo) - 1, Number(d), Number(hh), Number(mm), 0, 0)
  return isValidDate(local) ? local.toISOString() : null
}

/** True if two inputs represent the same calendar day (local). */
export function isSameDay(
  a?: string | Date | null,
  b?: string | Date | null
): boolean {
  if (a == null || b == null) return false;

  const da = toDate(a);
  const db = toDate(b);
  if (!da || !db) return false;

  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}